import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Check } from 'lucide-react';
import { SlideStage } from '../../components/deck/SlideStage';
import { MissedCallCounter } from '../../components/deck/MissedCallCounter';
import { VexuCallsShowcase } from '../../components/deck/VexuCallsShowcase';
import { capabilitySteps } from '../../data/deckSlide3';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
const MISSED = '#B4543A';
const T = {
  headline: 0.2,
  support: 0.9,
  missed: 1.3,
  arrow: 1.7,
  handled: 2.1,
  phone: 2.7,
  before: 3.7,
  after: 4.2,
  framework: 5.0
};
export function SolutionSlide() {
  return <SlideStage data-id="element-2656" data-mp="1zwcfqqt7igve">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2657" data-mp="1zwvmrchbjkun">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 90% 70% at 8% -18%, rgba(0,200,83,0.07) 0%, rgba(250,252,249,0) 60%), radial-gradient(ellipse 55% 60% at 84% 42%, rgba(0,200,83,0.10) 0%, rgba(250,252,249,0) 68%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 55%, #EFF3ED 100%)'
      }} data-id="element-2658" data-mp="2wfti4gr54cge" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2659" data-mp="11lzn5ovtgdk6">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2660" data-mp="xkebg6v0q5bk" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2661" data-mp="xibfz7b3jjzg">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2662" data-mp="nlovwkqlgwyh">
          03 · The Solution
        </motion.span>

        {/* Headline */}
        <h1 className="absolute text-[48px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 148,
        width: 720
      }} data-id="element-2663" data-mp="2bk953do9t8wm">
          {['From missed calls', 'to handled calls.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: T.headline + i * 0.16,
          ease
        }} data-id="element-2664" data-mp="22kw2n4ykdkle" data-mpi={i}>
              {line}
            </motion.span>)}
        </h1>

        <motion.p className="absolute text-[18px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 282,
        width: 500
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: T.support,
        ease
      }} data-id="element-2665" data-mp="2pho1x3gusiym">
          VEXU answers, understands and handles what it can — while you
          continue your day.
        </motion.p>

        {/* The transformation */}
        <div className="absolute" style={{
        left: 112,
        top: 362,
        width: 560
      }} data-id="element-2666" data-mp="14r36ysv94x6b">
          <motion.div className="flex items-center gap-3" initial={{
          opacity: 0
        }} animate={{
          opacity: 0.55
        }} transition={{
          duration: 0.9,
          delay: T.missed,
          ease
        }} data-id="element-2667" data-mp="18r5az8ogm2or">
            <span className="text-[22px] font-semibold tabular-nums line-through decoration-[1.5px]" style={{
            color: MISSED
          }} data-id="element-2668" data-mp="1ip1kjw1r0pkr">
              17
            </span>
            <span className="text-[13px] font-semibold uppercase tracking-[0.26em] line-through decoration-[1.5px]" style={{
            color: MISSED
          }} data-id="element-2669" data-mp="1sgxnbkes53vb">
              Missed calls
            </span>
          </motion.div>

          <motion.div className="mt-3 mb-4 flex items-center" initial={{
          opacity: 0,
          y: -6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: T.arrow,
          ease
        }} data-id="element-2670" data-mp="26f5ns2b0ynb8">
            <ArrowDown className="h-5 w-5 text-vexu-600" strokeWidth={1.5} data-id="element-2671" data-mp="1nsu0i6cb4zbv" />
          </motion.div>

          <MissedCallCounter to={17} delay={T.handled} label={<>
                Handled
                <br data-id="element-2673" data-mp="1xlreq0a9o24o" />
                calls
              </>} numberClassName="text-[108px] leading-[0.82] text-vexu-700" labelClassName="text-[14px] text-vexu-700/75" data-id="element-2672" data-mp="22h6ooh55ldvt" />
        </div>

        {/* Before / after on the same caller */}
        <div className="absolute" style={{
        left: 112,
        top: 562,
        width: 660
      }} data-id="element-2674" data-mp="1jtbhxh7yl0c7">
          <motion.div className="border-t border-[#0B1712]/[0.09] pt-4" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: T.before,
          ease
        }} data-id="element-2675" data-mp="1es45h4uvfcz7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-[#A9B5AC]" data-id="element-2676" data-mp="2xrlfgtz90cxa">
              Before
            </p>
            <p className="mt-2.5 text-[19px] leading-snug text-[#8A998F]" data-id="element-2677" data-mp="32rptmdcoovkc">
              <span className="font-semibold text-[#5A6B60]" data-id="element-2678" data-mp="2orf2rabdjgz6">John Smith</span>
              <span className="mx-2.5 text-[#C6CFC7]" data-id="element-2679" data-mp="2ot9lqj334z0m">·</span>
              <span style={{
              color: `${MISSED}CC`
            }} data-id="element-2680" data-mp="2st0mnfmbsviv">Missed call · 12:36</span>
            </p>
          </motion.div>

          <motion.div className="mt-6 border-t border-vexu-600/25 pt-4" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: T.after,
          ease
        }} data-id="element-2681" data-mp="3gk62hus9nnt6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-vexu-700/80" data-id="element-2682" data-mp="3bquv9sjjl50q">
              With VEXU
            </p>
            <p className="mt-2.5 text-[19px] leading-snug text-[#33463B]" data-id="element-2683" data-mp="39zx2qdow8sz">
              <span className="font-semibold text-[#0B1712]" data-id="element-2684" data-mp="83b7f1hpn7pn">John Smith</span>
              <span className="mx-2.5 text-[#C6CFC7]" data-id="element-2685" data-mp="hyrvk1r2npg8">·</span>
              Requested a proposal review meeting
            </p>
            <p className="mt-2.5 flex items-center gap-2 text-[17px] font-semibold text-vexu-700" data-id="element-2686" data-mp="dxznvcxulsm2">
              <Check className="h-[18px] w-[18px]" strokeWidth={2.6} data-id="element-2687" data-mp="8wa8jl3less6" />
              Meeting booked · Tomorrow · 11:00
            </p>
          </motion.div>
        </div>

        {/* RIGHT — the real Vexu Calls experience */}
        <motion.div className="absolute" style={{
        left: 1112,
        top: 162
      }} initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.2,
        delay: T.phone,
        ease
      }} data-id="element-2688" data-mp="3v6wdxozpewsh">
          <VexuCallsShowcase scale={0.68} data-id="element-2689" data-mp="3he6fgl2znzeh" />
        </motion.div>

        {/* The handling loop */}
        <motion.div className="absolute h-px bg-[#0B1712]/[0.09]" initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} style={{
        left: 112,
        right: 112,
        bottom: 128,
        transformOrigin: 'left'
      }} transition={{
        duration: 1.2,
        delay: T.framework - 0.2,
        ease
      }} data-id="element-2690" data-mp="3cek0o77iib3k" />
        <div className="absolute flex items-start" style={{
        left: 112,
        right: 112,
        bottom: 56
      }} data-id="element-2691" data-mp="329c396x9swsh">
          {capabilitySteps.map((step, i) => <motion.div key={step.label} className="flex items-start" style={{
          flex: step.detail ? 1 : 0
        }} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: T.framework + i * 0.15,
          ease
        }} data-id="element-2692" data-mp="3c89r07mvgmaf" data-mpi={i}>
              {i > 0 && <span className="mr-6 mt-[3px] text-[12px] text-[#C0CBC2]" data-id="element-2693" data-mp="2taqkpbrsthrp">
                  →
                </span>}
              <div className="pr-8" data-id="element-2694" data-mp="1vs73ske9dhm5">
                <p className="text-[12px] font-semibold uppercase tracking-[0.24em] text-[#0B1712]" data-id="element-2695" data-mp="20suqwuzd9eb3">
                  {step.label}
                </p>
                {step.detail && <p className="mt-2 text-[13px] leading-snug text-[#8A998F]" data-id="element-2696" data-mp="3ayivymj74tiu">
                    {step.detail}
                  </p>}
              </div>
            </motion.div>)}
        </div>
      </div>
    </SlideStage>;
}